import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ValueType } from 'react-select';

import { IOption } from '~/types';
import { formatLocationGroupOptions } from '~/utils/clientAllocation';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';

import MultiValue from '../../../components/MultiValue/MultiValue';
import Option from '../../../components/Option/Option';
import {
  IEditAllocationFormValues,
  LocationGroupWithAllocation,
} from '../../../shared/model/types';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import { normalizeLocationGroupOptionToAllocation } from '../../utils';

interface IProps {
  teamMemberIdx: number;
  updateLocationGroupAllocations: (groups: LocationGroupWithAllocation[]) => void;
  activeClientIds: number[];
  onDeleteClient: (groupId: number, clientId: number) => void;
  onDeleteGroup: (groupId: number) => void;
}

const AllocationLocationGroupsSelect: FC<IProps> = ({
  teamMemberIdx,
  updateLocationGroupAllocations,
  activeClientIds,
  onDeleteClient,
  onDeleteGroup,
}): ReactElement => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<IEditAllocationFormValues>();
  const { locationGroups } = useEditClientAllocationContext();

  const selectedFieldName =
    `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupIds` as const;

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );

  const options = useMemo(() => formatLocationGroupOptions(locationGroups), [locationGroups]);

  const existingByGroupId = useMemo(
    () => new Map(locationGroupAllocations.map(lg => [lg.locationGroupId, lg])),
    [locationGroupAllocations],
  );

  const handleSelect = useCallback(
    (selected: ValueType<IOption | IOption[], boolean>) => {
      const opts = (Array.isArray(selected) ? selected : []) as IOption[];
      const next = opts.map(
        opt =>
          existingByGroupId.get(Number(opt.value)) ??
          normalizeLocationGroupOptionToAllocation(Number(opt.value), opt.label),
      );
      updateLocationGroupAllocations(next);
    },
    [existingByGroupId, updateLocationGroupAllocations],
  );

  const handleSelectGroup = useCallback(
    (groupId: number) => {
      if (existingByGroupId.has(groupId)) {
        onDeleteGroup(groupId);
      } else {
        const groupOption = options.find(o => Number(o.value) === groupId);
        if (groupOption) {
          updateLocationGroupAllocations([
            ...Array.from(existingByGroupId.values()),
            normalizeLocationGroupOptionToAllocation(groupId, groupOption.label),
          ]);
        }
      }
    },
    [existingByGroupId, onDeleteGroup, options, updateLocationGroupAllocations],
  );

  return (
    <SelectComponent
      name={selectedFieldName}
      control={control}
      errors={errors}
      closeMenuOnSelect={false}
      menuPortalTarget={null}
      isRelativeWindow={false}
      showCustomComponents
      hideSelectedOptions={false}
      isMulti
      label="Select Location Groups"
      options={options}
      onSelect={handleSelect}
      customComponents={{
        MenuList: props => <MenuList {...props} label="Location Groups" />,
        MultiValue: props => (
          <MultiValue
            {...props}
            clientIds={activeClientIds}
            locationGroups={locationGroups}
            onDelete={(clientId: number) => onDeleteClient(Number(props.data.value), clientId)}
            onDeleteGroup={onDeleteGroup}
          />
        ),
        Option: props => <Option {...props} selectGroup={handleSelectGroup} />,
      }}
    />
  );
};

export default AllocationLocationGroupsSelect;
